import * as React from "react";
import { useRoute } from "wouter";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Loader2, ArrowLeft, Calendar, DollarSign, User, FileText, Trash2, Search, Receipt, MapPin } from "lucide-react";
import { transferPlanService } from "@/services/transferPlanService";
import { toast } from "sonner";
import { Link } from "wouter";

interface TransferPlan {
  _id: string;
  batchName: string;
  planId: string;
  summary: {
    totalRows: number;
    matchedCount: number;
    unmatchedCount: number;
    totalAmount: number;
  };
  totalWimaxIncome: number;
  totalOrpusIncome: number;
  totalOrdaIncome: number;
  totalOrlokIncome: number;
  matchedLocation: boolean;
  createdBy?: {
    _id: string;
    callsign?: string;
    nama_lengkap?: string;
    email?: string;
  };
  createdAt: string;
  updatedAt: string;
}

interface Transaction {
  _id: string;
  paymentId?: string;
  status: string;
  province?: string;
  kabkot?: string;
  wimaxIncome?: number;
  orpusIncome?: number;
  ordaIncome?: number;
  orlokIncome?: number;
  batchName?: string;
  user?: {
    _id: string;
    callsign?: string;
    nama_lengkap?: string;
    email?: string;
  };
  paymentGatewayData?: any;
  createdAt: string;
  updatedAt: string;
}

interface LocationAggregation {
  cities: Array<{
    province: string;
    kabkot: string;
    count: number;
    totalAmount: number;
    totalWimaxIncome: number;
    totalOrpusIncome: number;
    totalOrdaIncome: number;
    totalOrlokIncome: number;
    bank?: string;
    accountNumber?: string;
    accountName?: string;
  }>;
  totalPayments: number;
  totalAmount: number;
  totalWimaxIncome: number;
  totalOrpusIncome: number;
  totalOrdaIncome: number;
  totalOrlokIncome: number;
}

export function TransferPlanDetailPage() {
  const [, params] = useRoute<{ id: string }>("/keuangan/transfer-plans/:id");
  const [plan, setPlan] = React.useState<TransferPlan | null>(null);
  const [transactions, setTransactions] = React.useState<Transaction[]>([]);
  const [transactionsLoading, setTransactionsLoading] = React.useState(false);
  const [locationAggregation, setLocationAggregation] = React.useState<LocationAggregation | null>(null);
  const [locationLoading, setLocationLoading] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [activeTab, setActiveTab] = React.useState("overview");
  const [searchQuery, setSearchQuery] = React.useState("");

  const planId = params?.id;

  React.useEffect(() => {
    if (!planId) {
      setError("Transfer plan ID not found");
      setLoading(false);
      return;
    }

    const fetchPlan = async () => {
      setLoading(true);
      setError(null);

      try {
        const planData = await transferPlanService.getTransferPlanById(planId);
        setPlan(planData);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : "Failed to fetch transfer plan";
        setError(errorMessage);
        toast.error("Gagal memuat rencana transfer", {
          description: errorMessage,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchPlan();
  }, [planId]);

  // Fetch transactions when transactions tab is active
  React.useEffect(() => {
    if (activeTab === "transactions" && planId && transactions.length === 0 && !transactionsLoading) {
      const fetchTransactions = async () => {
        setTransactionsLoading(true);
        try {
          const data = await transferPlanService.getTransferPlanTransactions(planId, { flat: true });
          setTransactions(data.transactions);
        } catch (err) {
          const errorMessage = err instanceof Error ? err.message : "Failed to fetch transactions";
          toast.error("Gagal memuat transaksi", {
            description: errorMessage,
          });
        } finally {
          setTransactionsLoading(false);
        }
      };

      fetchTransactions();
    }
  }, [activeTab, planId, transactions.length, transactionsLoading]);

  // Fetch location aggregation when distribusi tab is active
  React.useEffect(() => {
    if (activeTab === "distribusi" && planId && !locationAggregation && !locationLoading) {
      const fetchLocationAggregation = async () => {
        setLocationLoading(true);
        try {
          const data = await transferPlanService.getLocationAggregation(planId);
          setLocationAggregation(data);
        } catch (err) {
          const errorMessage = err instanceof Error ? err.message : "Gagal memuat data distribusi";
          toast.error("Gagal memuat data distribusi", {
            description: errorMessage,
          });
        } finally {
          setLocationLoading(false);
        }
      };

      fetchLocationAggregation();
    }
  }, [activeTab, planId, locationAggregation, locationLoading]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString("id-ID", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">Memuat rencana transfer...</p>
        </div>
      </div>
    );
  }

  if (error || !plan) {
    return (
      <div className="flex-1 space-y-4 p-6">
        <Link href="/keuangan/transfer-plans">
          <Button variant="outline" className="gap-2 mb-4">
            <ArrowLeft className="h-4 w-4" />
            Kembali ke Rencana Transfer
          </Button>
        </Link>
        <Card className="border-destructive">
          <CardContent className="pt-6">
            <p className="text-sm text-destructive">{error || "Rencana transfer tidak ditemukan"}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const handleDelete = async () => {
    const confirmed = window.confirm(
      `Apakah Anda yakin ingin menghapus rencana transfer "${plan.batchName}"?\n\nTindakan ini tidak dapat dibatalkan.`
    );
    if (!confirmed) return;

    try {
      const result = await transferPlanService.deleteTransferPlan(plan._id);
      toast.success("Rencana transfer berhasil dihapus", {
        description: result.updatedPaymentsCount > 0 
          ? `${result.updatedPaymentsCount} pembayaran diperbarui`
          : undefined,
      });
      // Redirect to list page
      window.location.href = "/keuangan/transfer-plans";
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to delete transfer plan";
      toast.error("Gagal menghapus rencana transfer", {
        description: errorMessage,
      });
    }
  };

  return (
    <div className="flex-1 space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link href="/keuangan/transfer-plans">
            <Button variant="outline" size="sm" className="gap-2">
              <ArrowLeft className="h-4 w-4" />
              Kembali
            </Button>
          </Link>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Detail Rencana Transfer</h1>
            <p className="text-muted-foreground mt-1">
              Lihat detail rencana transfer: {plan.batchName}
            </p>
          </div>
        </div>
        <Button
          variant="destructive"
          size="sm"
          className="gap-2"
          onClick={handleDelete}
        >
          <Trash2 className="h-4 w-4" />
          Hapus
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList>
          <TabsTrigger value="overview">Ringkasan</TabsTrigger>
          <TabsTrigger value="transactions">
            <Receipt className="h-4 w-4 mr-2" />
            Transaksi ({plan.summary.matchedCount})
          </TabsTrigger>
          <TabsTrigger value="distribusi">
            <MapPin className="h-4 w-4 mr-2" />
            Distribusi
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          <div className="grid gap-6 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Informasi Rencana
                </CardTitle>
                <CardDescription>Informasi dasar tentang rencana transfer ini</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <div className="text-sm font-medium text-muted-foreground mb-1">Nama Batch</div>
                  <div className="text-lg font-mono font-semibold">{plan.batchName}</div>
                </div>
                <div>
                  <div className="text-sm font-medium text-muted-foreground mb-1">ID Rencana</div>
                  <div className="text-sm font-mono text-muted-foreground">{plan.planId}</div>
                </div>
                <div>
                  <div className="text-sm font-medium text-muted-foreground mb-1 flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    Dibuat Pada
                  </div>
                  <div className="text-sm">{formatDate(plan.createdAt)}</div>
                </div>
                {plan.createdBy && (
                  <div>
                    <div className="text-sm font-medium text-muted-foreground mb-1 flex items-center gap-2">
                      <User className="h-4 w-4" />
                      Dibuat Oleh
                    </div>
                    <div className="text-sm">
                      <div className="font-medium">{plan.createdBy.callsign || "-"}</div>
                      <div className="text-muted-foreground">{plan.createdBy.nama_lengkap || "-"}</div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <DollarSign className="h-5 w-5" />
                  Statistik Ringkasan
                </CardTitle>
                <CardDescription>Hasil pemrosesan dan jumlah</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <div className="text-sm font-medium text-muted-foreground mb-1">Total Baris</div>
                  <div className="text-lg font-semibold">{plan.summary.totalRows}</div>
                </div>
                <div>
                  <div className="text-sm font-medium text-muted-foreground mb-1">Pembayaran yang Cocok</div>
                  <div className="text-lg font-semibold text-green-600">{plan.summary.matchedCount}</div>
                </div>
                <div>
                  <div className="text-sm font-medium text-muted-foreground mb-1">Pesanan yang Tidak Cocok</div>
                  <div className="text-lg font-semibold text-orange-600">{plan.summary.unmatchedCount}</div>
                </div>
                <div>
                  <div className="text-sm font-medium text-muted-foreground mb-1">Total Jumlah</div>
                  <div className="text-lg font-semibold">{formatCurrency(plan.summary.totalAmount)}</div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Rincian Pendapatan</CardTitle>
                <CardDescription>Pendapatan yang dihitung untuk rencana transfer ini</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <div className="text-sm font-medium text-muted-foreground mb-1">Pendapatan Wimax</div>
                  <div className="text-lg font-semibold">{formatCurrency(plan.totalWimaxIncome)}</div>
                </div>
                <div>
                  <div className="text-sm font-medium text-muted-foreground mb-1">Pendapatan ORPUS</div>
                  <div className="text-lg font-semibold">{formatCurrency(plan.totalOrpusIncome)}</div>
                </div>
                <div>
                  <div className="text-sm font-medium text-muted-foreground mb-1">Pendapatan ORDA</div>
                  <div className="text-lg font-semibold">{formatCurrency(plan.totalOrdaIncome)}</div>
                </div>
                <div>
                  <div className="text-sm font-medium text-muted-foreground mb-1">Pendapatan ORLOK</div>
                  <div className="text-lg font-semibold">{formatCurrency(plan.totalOrlokIncome)}</div>
                </div>
                <div className="pt-2 border-t">
                  <div className="text-sm font-medium text-muted-foreground mb-1">Total Pendapatan</div>
                  <div className="text-xl font-bold">
                    {formatCurrency(
                      plan.totalWimaxIncome + plan.totalOrpusIncome + plan.totalOrdaIncome + plan.totalOrlokIncome
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Status Lokasi</CardTitle>
                <CardDescription>Apakah semua pembayaran yang cocok memiliki data lokasi</CardDescription>
              </CardHeader>
              <CardContent>
                <div>
                  <div className="text-sm font-medium text-muted-foreground mb-2">Lokasi yang Cocok</div>
                  {plan.matchedLocation ? (
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                      ✓ Semua pembayaran yang cocok memiliki data lokasi (provinsi dan kabupaten/kota)
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
                      ✗ Beberapa pembayaran yang cocok tidak memiliki data lokasi
                    </span>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="transactions" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Permintaan Pembayaran</CardTitle>
              <CardDescription>
                Semua permintaan pembayaran yang termasuk dalam batch rencana transfer ini
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="mb-4">
                <div className="relative">
                  <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Cari berdasarkan ID pembayaran, panggilan, email, atau lokasi..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-8"
                  />
                </div>
              </div>

              {transactionsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground mr-2" />
                  <span className="text-muted-foreground">Memuat transaksi...</span>
                </div>
              ) : transactions.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  Tidak ada transaksi ditemukan
                </div>
              ) : (
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>ID Pembayaran</TableHead>
                        <TableHead>Pengguna</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Lokasi</TableHead>
                        <TableHead className="text-right">Jumlah</TableHead>
                        <TableHead className="text-right">Pendapatan Wimax</TableHead>
                        <TableHead className="text-right">Pendapatan ORPUS</TableHead>
                        <TableHead className="text-right">Pendapatan ORDA</TableHead>
                        <TableHead className="text-right">Pendapatan ORLOK</TableHead>
                        <TableHead>Dibuat Pada</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {transactions
                        .filter((tx) => {
                          if (!searchQuery.trim()) return true;
                          const query = searchQuery.toLowerCase();
                          return (
                            tx.paymentId?.toLowerCase().includes(query) ||
                            tx.user?.callsign?.toLowerCase().includes(query) ||
                            tx.user?.email?.toLowerCase().includes(query) ||
                            tx.province?.toLowerCase().includes(query) ||
                            tx.kabkot?.toLowerCase().includes(query)
                          );
                        })
                        .map((tx) => {
                          const amount = tx.paymentGatewayData?.gross_amount || 
                                        tx.paymentGatewayData?.transaction_details?.gross_amount || 
                                        0;
                          return (
                            <TableRow key={tx._id}>
                              <TableCell className="font-mono text-sm">
                                {tx.paymentId ? tx.paymentId.split("-")[0] : "-"}
                              </TableCell>
                              <TableCell>
                                <div>
                                  <div className="font-medium">{tx.user?.callsign || "-"}</div>
                                  <div className="text-sm text-muted-foreground">{tx.user?.nama_lengkap || "-"}</div>
                                  <div className="text-xs text-muted-foreground">{tx.user?.email || "-"}</div>
                                </div>
                              </TableCell>
                              <TableCell>
                                <Badge variant={tx.status === "Settlement" ? "default" : "secondary"}>
                                  {tx.status}
                                </Badge>
                              </TableCell>
                              <TableCell>
                                <div>
                                  <div className="text-sm">{tx.province || "-"}</div>
                                  <div className="text-xs text-muted-foreground">{tx.kabkot || "-"}</div>
                                </div>
                              </TableCell>
                              <TableCell className="text-right">{formatCurrency(amount)}</TableCell>
                              <TableCell className="text-right">
                                {tx.wimaxIncome ? formatCurrency(tx.wimaxIncome) : "-"}
                              </TableCell>
                              <TableCell className="text-right">
                                {tx.orpusIncome ? formatCurrency(tx.orpusIncome) : "-"}
                              </TableCell>
                              <TableCell className="text-right">
                                {tx.ordaIncome ? formatCurrency(tx.ordaIncome) : "-"}
                              </TableCell>
                              <TableCell className="text-right">
                                {tx.orlokIncome ? formatCurrency(tx.orlokIncome) : "-"}
                              </TableCell>
                              <TableCell className="text-sm text-muted-foreground">
                                {formatDate(tx.createdAt)}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="distribusi" className="space-y-6">
          {locationLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground mr-2" />
              <span className="text-muted-foreground">Memuat data distribusi...</span>
            </div>
          ) : !locationAggregation ? (
            <Card>
              <CardContent className="pt-6">
                <div className="text-center py-12 text-muted-foreground">
                  Data distribusi tidak tersedia
                </div>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Summary Card */}
              <Card>
                <CardHeader>
                  <CardTitle>Ringkasan Total</CardTitle>
                  <CardDescription>
                    Statistik keseluruhan untuk semua lokasi
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                    <div>
                      <div className="text-sm text-muted-foreground">Total Pembayaran</div>
                      <div className="text-lg font-semibold">{locationAggregation.totalPayments}</div>
                    </div>
                    <div>
                      <div className="text-sm text-muted-foreground">Total Jumlah</div>
                      <div className="text-lg font-semibold">{formatCurrency(locationAggregation.totalAmount)}</div>
                    </div>
                    <div>
                      <div className="text-sm text-muted-foreground">Total Pendapatan Wimax</div>
                      <div className="text-lg font-semibold">{formatCurrency(locationAggregation.totalWimaxIncome)}</div>
                    </div>
                    <div>
                      <div className="text-sm text-muted-foreground">Total Pendapatan ORPUS</div>
                      <div className="text-lg font-semibold">{formatCurrency(locationAggregation.totalOrpusIncome)}</div>
                    </div>
                    <div>
                      <div className="text-sm text-muted-foreground">Total Pendapatan ORDA</div>
                      <div className="text-lg font-semibold">{formatCurrency(locationAggregation.totalOrdaIncome)}</div>
                    </div>
                    <div>
                      <div className="text-sm text-muted-foreground">Total Pendapatan ORLOK</div>
                      <div className="text-lg font-semibold">{formatCurrency(locationAggregation.totalOrlokIncome)}</div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Location Breakdown Table */}
              <Card>
                <CardHeader>
                  <CardTitle>Rincian Lokasi</CardTitle>
                  <CardDescription>
                    Provinsi Transfer Location Plan dan Kota/Kabupaten Location Transfer Plan
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {(() => {
                    // Group cities by province
                    const provinceMap = new Map<string, {
                      province: string;
                      cities: typeof locationAggregation.cities;
                      totalCount: number;
                      totalAmount: number;
                      totalWimaxIncome: number;
                      totalOrpusIncome: number;
                      totalOrdaIncome: number;
                      totalOrlokIncome: number;
                      bank?: string;
                      accountNumber?: string;
                      accountName?: string;
                    }>();

                    for (const city of locationAggregation.cities) {
                      if (!provinceMap.has(city.province)) {
                        provinceMap.set(city.province, {
                          province: city.province,
                          cities: [],
                          totalCount: 0,
                          totalAmount: 0,
                          totalWimaxIncome: 0,
                          totalOrpusIncome: 0,
                          totalOrdaIncome: 0,
                          totalOrlokIncome: 0,
                        });
                      }

                      const province = provinceMap.get(city.province)!;
                      province.cities.push(city);
                      province.totalCount += city.count;
                      province.totalAmount += city.totalAmount;
                      province.totalWimaxIncome += city.totalWimaxIncome;
                      province.totalOrpusIncome += city.totalOrpusIncome;
                      province.totalOrdaIncome += city.totalOrdaIncome;
                      province.totalOrlokIncome += city.totalOrlokIncome;
                    }

                    const groupedByProvince = Array.from(provinceMap.values());

                    return groupedByProvince.length === 0 ? (
                      <div className="text-center py-12 text-muted-foreground">
                        Tidak ada data lokasi tersedia
                      </div>
                    ) : (
                      <div className="rounded-md border">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Ref ID</TableHead>
                              <TableHead className="w-[200px]">Lokasi</TableHead>
                              <TableHead className="text-right">Total Transaksi</TableHead>
                              <TableHead className="text-right">Jumlah Akumulatif</TableHead>
                              <TableHead>Bank</TableHead>
                              <TableHead>No rekening</TableHead>
                              <TableHead>Nama pemegang rekening</TableHead>
                              <TableHead>Aksi</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {groupedByProvince.map((provinceData, provinceIdx) => (
                              <React.Fragment key={provinceIdx}>
                                {/* Province Row */}
                                <TableRow className="bg-muted/50 font-semibold">
                                  <TableCell className="font-semibold font-mono text-sm">
                                    {plan.batchName}-ORDA-{provinceIdx + 1}
                                  </TableCell>
                                  <TableCell className="font-semibold">
                                    {provinceData.province}
                                  </TableCell>
                                  <TableCell className="text-right font-semibold">
                                    {provinceData.totalCount}
                                  </TableCell>
                                  <TableCell className="text-right font-semibold">
                                    {formatCurrency(provinceData.totalOrdaIncome)}
                                  </TableCell>
                                  <TableCell className="font-semibold">
                                    {provinceData.bank || "-"}
                                  </TableCell>
                                  <TableCell className="font-semibold">
                                    {provinceData.accountNumber || "-"}
                                  </TableCell>
                                  <TableCell className="font-semibold">
                                    {provinceData.accountName || "-"}
                                  </TableCell>
                                  <TableCell className="font-semibold">
                                    {provinceData.bank && provinceData.accountNumber && provinceData.accountName ? (
                                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                                        confirm
                                      </span>
                                    ) : (
                                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
                                        hold
                                      </span>
                                    )}
                                  </TableCell>
                                </TableRow>
                                {/* City/Kabkot Rows */}
                                {provinceData.cities.map((city, cityIdx) => (
                                  <TableRow key={cityIdx} className="bg-background">
                                    <TableCell className="font-mono text-sm">
                                      {plan.batchName}-ORLOK-{provinceData.province.toUpperCase().replace(/\s+/g, "-")}-{cityIdx + 1}
                                    </TableCell>
                                    <TableCell className="text-left">
                                      {city.kabkot}
                                    </TableCell>
                                    <TableCell className="text-right">{city.count}</TableCell>
                                    <TableCell className="text-right">
                                      {formatCurrency(city.totalOrlokIncome)}
                                    </TableCell>
                                    <TableCell>{city.bank || "-"}</TableCell>
                                    <TableCell>{city.accountNumber || "-"}</TableCell>
                                    <TableCell>{city.accountName || "-"}</TableCell>
                                    <TableCell>
                                      {city.bank && city.accountNumber && city.accountName ? (
                                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                                          confirm
                                        </span>
                                      ) : (
                                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
                                          hold
                                        </span>
                                      )}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </React.Fragment>
                            ))}
                            {/* Wimax Income Row - At bottom of entire table */}
                            <TableRow className="bg-muted/30 font-semibold">
                              <TableCell className="font-semibold font-mono text-sm">
                                {plan.batchName}-WIMAX
                              </TableCell>
                              <TableCell className="font-semibold">
                                Wimax
                              </TableCell>
                              <TableCell className="text-right">-</TableCell>
                              <TableCell className="text-right font-semibold">
                                {formatCurrency(locationAggregation.totalWimaxIncome)}
                              </TableCell>
                              <TableCell>-</TableCell>
                              <TableCell>-</TableCell>
                              <TableCell>-</TableCell>
                              <TableCell>-</TableCell>
                            </TableRow>
                            {/* ORPUS Income Row - At bottom of entire table */}
                            <TableRow className="bg-muted/30 font-semibold">
                              <TableCell className="font-semibold font-mono text-sm">
                                {plan.batchName}-ORPUS
                              </TableCell>
                              <TableCell className="font-semibold">
                                ORPUS
                              </TableCell>
                              <TableCell className="text-right">-</TableCell>
                              <TableCell className="text-right font-semibold">
                                {formatCurrency(locationAggregation.totalOrpusIncome)}
                              </TableCell>
                              <TableCell>-</TableCell>
                              <TableCell>-</TableCell>
                              <TableCell>-</TableCell>
                              <TableCell>-</TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </div>
                    );
                  })()}
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

